/**
 * E018-S013-T012: DatabaseFullscreenEditor
 *
 * A Lit component that renders a single database in Page-Mode layout.
 * This is similar to PageEditor but shows only the database block,
 * hiding other elements like the surface, notes, and other databases.
 *
 * Key points:
 * - Uses std.render() to create EditorHost (like PageEditor)
 * - Uses FullscreenDatabaseViewExtension to force affine-database component
 * - Uses custom DocModeProvider that returns 'page' mode
 *   → Ghost nodes, automation nodes, and other canvas blocks hide themselves
 * - CSS hides surface, notes, and streamlain blocks as backup
 * - JavaScript makes ONLY the target database visible (overrides page-mode hide)
 *
 * E022-SPK023: Database Tabs (Excel-style)
 * - Shows tabs at the bottom for all databases in the current doc
 * - Allows switching between databases by clicking tabs
 * - Supports drag & drop reordering of tabs
 */
import { I18n } from '@affine/i18n';
import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  DatabaseSelection,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import {
  CALENDAR_DETAIL_TARGET_EVENT,
  type CalendarDetailTarget,
  CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT,
  CANVAS_VIEWPORT_TARGET_SCROLL_EVENT,
  type CanvasViewportTargetPosition,
  type DataSource,
} from '@blocksuite/affine/data-view';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { noop } from '@blocksuite/affine/global/utils';
import {
  BlockStdScope,
  EditorHost,
  ShadowlessElement,
} from '@blocksuite/affine/std';
import type { ExtensionType, Store } from '@blocksuite/affine/store';
import { deleteSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import { css, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import type { Text as YText } from 'yjs';

import {
  formatRowLabel,
  getVisibleRowWindowFromModel,
} from '../../blocksuite/ai/utils/database-metadata';
import {
  listUserDatabaseBlocks,
  orderCanonicalDatabaseCandidates,
} from '../utils/database-canonical-selection';

noop(EditorHost);

interface DatabaseTabInfo {
  id: string;
  title: string;
  titleLoading: boolean;
}

interface FocusCellPosition {
  columnId: string | null;
  columnIndex: number;
  groupKey?: string;
  rowId: string;
  rowIndex: number;
  viewId: string;
}

type FullscreenViewportParticipant = HTMLElement & {
  applyCanvasViewportTarget?: (
    targetId: string,
    position: CanvasViewportTargetPosition
  ) => boolean | void;
  getCanvasViewportTargets?: () => Record<string, CanvasViewportTargetPosition>;
};

type FullscreenViewportStoredViewState = {
  target?: string;
  targets: Record<string, CanvasViewportTargetPosition>;
  updatedAt: number;
};

type FullscreenViewportStoredState = {
  activeViewId: string | null;
  updatedAt: number;
  views: Record<string, FullscreenViewportStoredViewState>;
};

type FullscreenViewportRestoreState = FullscreenViewportStoredViewState & {
  gateKey: string;
  storageKey: string;
  viewId: string;
};

const DATABASE_TAB_TITLE_LOADING_LABEL = 'Loading...';
const DATABASE_FULLSCREEN_VIEWPORT_STORAGE_KEY_PREFIX =
  'sl:database-fullscreen-viewport:v1';
const DATABASE_FULLSCREEN_VIEWPORT_PARTICIPANT_SELECTOR =
  '[data-canvas-viewport-participant]';
const DATABASE_FULLSCREEN_VIEWPORT_RESTORE_MAX_ATTEMPTS = 120;
// Exact shard navigation normally settles in a few frames. Keep a short
// rendering allowance without masking missing locators behind a long retry.
const DATABASE_FULLSCREEN_FOCUS_MAX_ATTEMPTS = 120;
const DATABASE_FULLSCREEN_VIEWPORT_PERSIST_DELAY_MS = 150;

function escapeAttributeValue(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function sanitizeNonNegativeFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, value)
    : null;
}

function sanitizeViewportPosition(
  value: unknown
): CanvasViewportTargetPosition | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const position = value as Partial<CanvasViewportTargetPosition>;
  const scrollLeft = sanitizeNonNegativeFiniteNumber(position.scrollLeft);
  const scrollTop = sanitizeNonNegativeFiniteNumber(position.scrollTop);
  if (scrollLeft == null || scrollTop == null) {
    return null;
  }

  const rowAnchor =
    position.rowAnchor &&
    typeof position.rowAnchor === 'object' &&
    typeof position.rowAnchor.rowId === 'string'
      ? {
          offsetPx:
            sanitizeNonNegativeFiniteNumber(position.rowAnchor.offsetPx) ?? 0,
          offsetRatio:
            typeof position.rowAnchor.offsetRatio === 'number' &&
            Number.isFinite(position.rowAnchor.offsetRatio)
              ? Math.max(0, position.rowAnchor.offsetRatio)
              : 0,
          rowId: position.rowAnchor.rowId,
        }
      : undefined;

  return rowAnchor
    ? { rowAnchor, scrollLeft, scrollTop }
    : { scrollLeft, scrollTop };
}

function sanitizeViewportTargets(
  value: unknown
): Record<string, CanvasViewportTargetPosition> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([targetId, position]) => [
        targetId,
        sanitizeViewportPosition(position),
      ])
      .filter(
        (entry): entry is [string, CanvasViewportTargetPosition] =>
          typeof entry[0] === 'string' && entry[1] != null
      )
  );
}

function isFullscreenViewportTargetRestored(
  expected: CanvasViewportTargetPosition,
  actual: CanvasViewportTargetPosition | undefined
): boolean {
  if (!actual) return false;

  if (expected.rowAnchor?.rowId) {
    return actual.rowAnchor?.rowId === expected.rowAnchor.rowId;
  }

  return (
    Math.abs(actual.scrollLeft - expected.scrollLeft) <= 2 &&
    Math.abs(actual.scrollTop - expected.scrollTop) <= 2
  );
}

function getDatabaseCanonicalReferenceKey(tab: {
  id: string;
  model?: unknown;
}): string {
  const model = tab.model as
    | {
        props?: {
          databaseId?: unknown;
          databaseRootDocId?: unknown;
        };
      }
    | undefined;
  const databaseId =
    typeof model?.props?.databaseId === 'string' && model.props.databaseId
      ? model.props.databaseId
      : tab.id;
  const rootDocId =
    typeof model?.props?.databaseRootDocId === 'string' &&
    model.props.databaseRootDocId
      ? model.props.databaseRootDocId
      : null;

  return rootDocId ? `${rootDocId}:${databaseId}` : tab.id;
}

export type DatabaseSurfaceProfile = 'embedded' | 'fullscreen';

export class DatabaseFullscreenEditor extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    /* An open on-screen keyboard (--streamlain-keyboard-inset) lifts the tabs bar above it. */
    database-fullscreen-editor {
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      padding-bottom: var(--streamlain-keyboard-inset, 0px);
      min-height: 0;
      overflow: hidden;
      font-family: var(--streamlain-font-family);
      background: var(--streamlain-v2-layer-background-primary);
    }

    database-fullscreen-editor * {
      box-sizing: border-box;
    }

    .database-fullscreen-viewport {
      position: relative;
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: hidden;
      overscroll-behavior-x: none;
      overscroll-behavior-y: contain;
      touch-action: pan-y;
      container-name: viewport;
      container-type: inline-size;
    }

    .database-fullscreen-container {
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      height: auto;
      min-height: 0;
      width: 100%;
      overflow: hidden;
      padding: 0 12px;
    }

    .database-fullscreen-viewport[data-database-surface-profile='embedded']
      .database-fullscreen-container {
      padding: 0;
    }

    .database-fullscreen-viewport.viewport-restoring
      .database-fullscreen-container {
      visibility: hidden;
      pointer-events: none;
    }

    .database-fullscreen-container > editor-host,
    database-fullscreen-editor editor-host fullscreen-database-root,
    database-fullscreen-editor editor-host .fullscreen-database-root,
    database-fullscreen-editor editor-host fullscreen-database-note,
    database-fullscreen-editor editor-host .fullscreen-database-note,
    database-fullscreen-editor editor-host affine-database,
    database-fullscreen-editor
      editor-host
      affine-database
      > div[contenteditable='false'] {
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      height: auto;
      min-height: 0;
      max-height: none;
      overflow: hidden;
    }

    /* E022-SPK023: Database Tabs Bar (Excel-style) */
    .database-tabs-bar {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 4px 12px;
      background: var(--streamlain-v2-layer-background-secondary);
      border-top: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      min-height: 36px;
      overflow-x: auto;
      overflow-y: hidden;
      flex-shrink: 0;
    }

    .database-tabs-bar::-webkit-scrollbar {
      height: 4px;
    }

    .database-tabs-bar::-webkit-scrollbar-track {
      background: transparent;
    }

    .database-tabs-bar::-webkit-scrollbar-thumb {
      background: var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 2px;
    }

    /* E022-SPK023: Add new database button */
    .database-tab-add {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: transparent;
      border: 1px dashed var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 6px;
      cursor: pointer;
      color: var(--streamlain-v2-text-secondary);
      transition: all 0.15s ease;
      flex-shrink: 0;
      margin-right: 8px;
    }

    .database-tab-add:hover {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      border-color: var(--streamlain-v2-brand-primary);
      color: var(--streamlain-v2-brand-primary);
    }

    .database-tab-add svg {
      width: 16px;
      height: 16px;
    }

    .database-tab {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      line-height: 20px;
      background: var(--streamlain-v2-layer-background-primary);
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-bottom: none;
      border-radius: 6px 6px 0 0;
      cursor: pointer;
      font-size: 13px;
      color: var(--streamlain-v2-text-secondary);
      white-space: nowrap;
      min-width: 80px;
      max-width: 180px;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: all 0.15s ease;
      user-select: none;
      flex-shrink: 0;
    }

    .database-tab:hover {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      color: var(--streamlain-v2-text-primary);
    }

    .database-tab.active {
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-weight: 500;
      border-color: var(--streamlain-v2-brand-primary);
      border-bottom: 2px solid var(--streamlain-v2-layer-background-primary);
      margin-bottom: -1px;
    }

    .database-tab.dragging {
      opacity: 0.5;
      transform: scale(0.95);
    }

    .database-tab.drag-over {
      border-left: 2px solid var(--streamlain-v2-brand-primary);
    }

    .database-tab-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }

    .database-tab-title {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .database-tab-title.loading {
      color: var(--streamlain-v2-text-secondary);
      opacity: 0.65;
      font-style: italic;
    }

    /* E022-SPK023: Tab title input for inline editing */
    .database-tab-title-input {
      border: none;
      background: transparent;
      outline: none;
      font-size: 13px;
      font-family: inherit;
      color: inherit;
      padding: 0;
      margin: 0;
      width: 100%;
      min-width: 60px;
    }

    .database-tab-title-input:focus {
      background: var(--streamlain-v2-layer-background-secondary);
      padding: 2px 4px;
      border-radius: 4px;
    }

    /* Hide doc page padding */
    database-fullscreen-editor editor-host affine-doc-page {
      padding: 0 !important;
    }

    /* Hide surface (canvas background) */
    database-fullscreen-editor editor-host affine-surface {
      display: none !important;
    }

    /* Hide notes */
    database-fullscreen-editor editor-host affine-note {
      display: none !important;
    }

    /* Hide streamlain blocks (ghost nodes, automation nodes, etc.) - backup */
    database-fullscreen-editor editor-host affine-streamlain-block {
      display: none !important;
    }

    /* Hide ghost canvas blocks - backup */
    database-fullscreen-editor editor-host affine-ghost-canvas {
      display: none !important;
    }

    /* Hide automation canvas blocks - backup */
    database-fullscreen-editor editor-host affine-automation-canvas {
      display: none !important;
    }

    /* Style for the visible target database */
    database-fullscreen-editor editor-host affine-database {
      width: 100% !important;
      max-width: 100% !important;
    }

    database-fullscreen-editor
      .database-fullscreen-viewport[data-database-surface-profile='embedded']
      editor-host
      affine-database {
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 0 !important;
    }
  `;

  private _observer: MutationObserver | null = null;
  private _requestedViewStorageReadyCleanup: (() => void) | null = null;
  private _requestedViewStorageReadyKey: string | null = null;
  // The tab title lives in each database's storage document, which the page
  // document's block events do not report; each tab observes its title text.
  private readonly _tabTitleObservers = new Map<
    string,
    { yText: unknown; cleanup: () => void }
  >();
  private _selectionCleanup: (() => void) | null = null;
  private _focusCellFrame: ReturnType<typeof requestAnimationFrame> | null =
    null;
  private _fullscreenViewportPersistTimer: ReturnType<
    typeof setTimeout
  > | null = null;
  private _fullscreenViewportRestoreFrame: ReturnType<
    typeof requestAnimationFrame
  > | null = null;
  private _fullscreenViewportRestoreBlockedKey: string | null = null;
  private _fullscreenViewportRestoreSettledKey: string | null = null;
  private _activeFullscreenViewportRestoreState: FullscreenViewportRestoreState | null =
    null;
  private _pendingFullscreenViewportTargets: Record<
    string,
    CanvasViewportTargetPosition
  > = {};
  private _lastFullscreenViewportTargetId: string | null = null;
  private _lastConsumedFocusCellKey: string | null = null;
  private readonly _tabsBarRef = createRef<HTMLElement>();
  private readonly _viewportRef = createRef<HTMLElement>();

  // E022-SPK023: Database tabs state
  private _draggedTabId: string | null = null;
  private _dragOverTabId: string | null = null;

  // E022-SPK023: Tab editing state
  private _editingTabId: string | null = null;
  private _editingTabTitle: string = '';

  @state()
  private accessor _databaseTabs: DatabaseTabInfo[] = [];

  @state()
  private accessor _fullscreenViewportRestorePending = false;

  get host() {
    try {
      return this.std.host;
    } catch {
      return null;
    }
  }

  private get _isEmbeddedSurface() {
    return this.surfaceProfile === 'embedded';
  }

  private _surfaceFlag(
    value: boolean | null,
    fullscreenDefault: boolean,
    embeddedDefault: boolean
  ) {
    if (typeof value === 'boolean') return value;
    return this._isEmbeddedSurface ? embeddedDefault : fullscreenDefault;
  }

  private get _showDatabaseTabs() {
    return (
      !this.hideTabsBar && this._surfaceFlag(this.showDatabaseTabs, true, false)
    );
  }

  private get _showTitleRow() {
    return this._surfaceFlag(this.showTitleRow, true, false);
  }

  private get _showToolbarRow() {
    return this._surfaceFlag(this.showToolbarRow, true, true);
  }

  private get _showViewBar() {
    return this._surfaceFlag(this.showViewBar, true, false);
  }

  private get _showTools() {
    return this._surfaceFlag(this.showTools, true, true);
  }

  private get _showQuickSettings() {
    return this._surfaceFlag(this.showQuickSettings, true, false);
  }

  private get _showInlineLargeDataBoundary() {
    return this._surfaceFlag(this.showInlineLargeDataBoundary, true, false);
  }

  private get _showRowWindowFooter() {
    return this._surfaceFlag(this.showRowWindowFooter, true, false);
  }

  private get _forceRowWindowControls() {
    return this._surfaceFlag(this.forceRowWindowControls, true, false);
  }

  private get _emitFullscreenEvents() {
    return this._surfaceFlag(this.emitFullscreenEvents, true, false);
  }

  private get _persistViewportState() {
    return this._surfaceFlag(this.persistViewportState, true, false);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.dataset.colourIsland = 'document';

    // E022-SPK023: Subscribe to doc changes for reactive tab updates
    this._disposables.add(
      this.doc.slots.rootAdded.subscribe(() => {
        this.requestUpdate();
        this._updateDatabaseTabs();
      })
    );

    // E022-SPK023: Subscribe to block updates (add/remove/update)
    this._disposables.add(
      this.doc.slots.blockUpdated.subscribe(({ type, id }) => {
        // Check if a database was added, removed, or updated
        const block = this.doc.getBlock(id);
        // Refresh tabs on add/delete, or when a database block is updated (e.g., title change)
        if (
          type === 'add' ||
          type === 'delete' ||
          (type === 'update' && block?.flavour === 'affine:database')
        ) {
          this._updateDatabaseTabs();
        }
      })
    );

    this.std = new BlockStdScope({
      store: this.doc,
      extensions: this.specs,
    });
    // Initial tab discovery
    this._updateDatabaseTabs();
    this._applyRequestedView();

    // sl-wo7: Listen for rename events from React context menu
    this._handleRenameEvent = this._handleRenameEvent.bind(this);
    if (this._showDatabaseTabs) {
      window.addEventListener(
        'streamlain:database-tab-rename',
        this._handleRenameEvent
      );
    }
    if (this._showViewBar) {
      window.addEventListener(
        'streamlain:database-view-change',
        this._handleDatabaseViewChange
      );
    }

    // sl-57s.1.2: Notify AI context about fullscreen mode open
    this._dispatchFullscreenContextEvent('open');

    // sl-28ek: Track row selection for AI context chip
    if (this._emitFullscreenEvents) {
      this._setupRowSelectionTracking();
    }
    if (this._persistViewportState) {
      this.addEventListener(
        CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT,
        this._handleFullscreenViewportParticipantReady
      );
      this.addEventListener(
        CANVAS_VIEWPORT_TARGET_SCROLL_EVENT,
        this._handleFullscreenViewportTargetScroll
      );
      this._scheduleFullscreenViewportRestore(0);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._focusCellFrame !== null) {
      cancelAnimationFrame(this._focusCellFrame);
      this._focusCellFrame = null;
    }
    if (this._fullscreenViewportRestoreFrame !== null) {
      cancelAnimationFrame(this._fullscreenViewportRestoreFrame);
      this._fullscreenViewportRestoreFrame = null;
    }
    this._fullscreenViewportRestoreSettledKey = null;
    this._activeFullscreenViewportRestoreState = null;
    if (this._fullscreenViewportPersistTimer !== null) {
      clearTimeout(this._fullscreenViewportPersistTimer);
      this._fullscreenViewportPersistTimer = null;
    }
    if (this._persistViewportState) {
      this._flushPendingFullscreenViewportPersist();
      this._persistCurrentFullscreenViewportTargets();
    } else {
      this._pendingFullscreenViewportTargets = {};
    }
    this._observer?.disconnect();
    this._observer = null;
    this._teardownRequestedViewStorageReadyObserver();
    for (const observer of this._tabTitleObservers.values()) {
      observer.cleanup();
    }
    this._tabTitleObservers.clear();
    this._selectionCleanup?.();
    this._selectionCleanup = null;
    window.removeEventListener(
      'streamlain:database-tab-rename',
      this._handleRenameEvent
    );
    window.removeEventListener(
      'streamlain:database-view-change',
      this._handleDatabaseViewChange
    );
    this.removeEventListener(
      CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT,
      this._handleFullscreenViewportParticipantReady
    );
    this.removeEventListener(
      CANVAS_VIEWPORT_TARGET_SCROLL_EVENT,
      this._handleFullscreenViewportTargetScroll
    );

    // sl-57s.1.2: Notify AI context about fullscreen mode close
    this._dispatchFullscreenContextEvent('close');
  }

  /**
   * sl-57s.1.2: Dispatch fullscreen context event for AI auto-injection
   */
  private _dispatchFullscreenContextEvent(
    action: 'open' | 'close' | 'tab-switch',
    databaseId = this.databaseId
  ) {
    if (!this._emitFullscreenEvents) return;

    if (databaseId === '__placeholder__') {
      window.dispatchEvent(
        new CustomEvent('streamlain:fullscreen-context', {
          detail: {
            action: 'close',
            databaseId,
            databaseName: '',
            docId: this.doc.id,
          },
        })
      );
      return;
    }

    const tab = this._databaseTabs.find(t => t.id === databaseId);
    window.dispatchEvent(
      new CustomEvent('streamlain:fullscreen-context', {
        detail: {
          action,
          databaseId,
          databaseName:
            tab?.title ||
            I18n.t('com.streamlain.ai.context.canvas.type.database'),
          docId: this.doc.id,
        },
      })
    );
  }

  /** Read current native table focus from its existing rendered window; never the requested URL row. */
  getFocusedRowId(): string | null {
    const logic = this._findVirtualTableUi()?.logic;
    const view = logic?.view;
    if (!view || !this.doc.getBlock(this.databaseId)) return null;
    let visited = 0;
    for (const selection of this.std.selection.value) {
      if (visited++ >= 128) break;
      if (
        !(selection instanceof DatabaseSelection) ||
        !selection.isOn(this.databaseId, 'fullscreen')
      )
        continue;
      const current = selection.viewSelection;
      if (current.type !== 'table' || current.viewId !== view.id) return null;
      const window = view.getBoundedRowsWindow().rowIds;
      if (current.selectionType === 'row') {
        const id = current.rows[0]?.id;
        if (!id) return null;
        for (let index = 0; index < Math.min(window.length, 128); index++)
          if (window[index] === id) return id;
        return null;
      }
      const index = current.focus.rowIndex;
      if (!Number.isInteger(index) || index < 0) return null;
      if (!current.groupKey) return window[index] ?? null;
      const groups = (
        logic as {
          ['groups$']?: {
            value?: Array<{ id: string; rows: readonly string[] }>;
          };
        }
      ).groups$?.value;
      if (!groups) return null;
      for (let n = 0; n < Math.min(groups.length, 128); n++) {
        if (groups[n].id === current.groupKey)
          return groups[n].rows[index] ?? null;
      }
      return null;
    }
    return null;
  }

  /**
   * sl-28ek: Subscribe to selection changes and dispatch row-selection events.
   * Uses the store's selection manager to detect when rows are checked/unchecked.
   */
  private _setupRowSelectionTracking() {
    const selection = this.std.selection;
    const sub = selection.slots.changed.subscribe(selections => {
      const dbSel = selections.find(
        (s): s is DatabaseSelection =>
          s instanceof DatabaseSelection &&
          s.isOn(this.databaseId, 'fullscreen')
      );

      if (!dbSel) {
        this._dispatchRowSelectionEvent([], '');
        return;
      }
      if (dbSel.viewSelection.selectionType !== 'row') {
        this._dispatchRowSelectionEvent([], '');
        return;
      }

      const rows = (dbSel.viewSelection as { rows: Array<{ id: string }> })
        .rows;
      const rowIds = rows.map(r => r.id);

      const dbBlock = this.doc.getBlock(this.databaseId);
      const selectedRowLabel = dbBlock
        ? this._formatSelectedRowsFromView(rowIds, dbBlock.model)
        : `${rowIds.length} selected rows`;

      this._dispatchRowSelectionEvent(
        rowIds,
        selectedRowLabel,
        dbBlock ? getVisibleRowWindowFromModel(dbBlock.model) : undefined
      );
    });

    this._selectionCleanup = () => sub.unsubscribe();
  }

  private _resolveFullscreenViewportIdentity(): {
    databaseId: string;
    databaseRootDocId: string;
    userId: string;
    workspaceId: string;
  } | null {
    const dbBlock = this.doc.getBlock(this.databaseId);
    if (!dbBlock) return null;

    const dataSource = createDatabaseBlockDataSource(dbBlock.model as any);
    const databaseId = dataSource.databaseId;
    const databaseRootDocId = dataSource.storageModel.props.databaseRootDocId;
    if (!databaseRootDocId) {
      throw new Error(`Database "${databaseId}" names no root document.`);
    }

    return {
      databaseId,
      databaseRootDocId,
      userId: this.userId || '__local__',
      workspaceId: this.workspaceId || '__workspace__',
    };
  }

  private _resolveCurrentFullscreenViewportViewId(
    model: unknown
  ): string | null {
    if (this.viewId) return this.viewId;

    try {
      const dataSource = createDatabaseBlockDataSource(model as any);
      dataSource.setUiStatePersistence('local');
      return dataSource.viewManager.currentView$.value?.id ?? null;
    } catch {
      return null;
    }
  }

  private _fullscreenViewportStorageKey(): string | null {
    const identity = this._resolveFullscreenViewportIdentity();
    if (!identity) return null;

    return [
      DATABASE_FULLSCREEN_VIEWPORT_STORAGE_KEY_PREFIX,
      identity.userId,
      identity.workspaceId,
      identity.databaseRootDocId,
      identity.databaseId,
    ]
      .map(encodeURIComponent)
      .join(':');
  }

  private _readFullscreenViewportState(
    key = this._fullscreenViewportStorageKey()
  ): FullscreenViewportStoredState | null {
    if (!key || typeof localStorage === 'undefined') return null;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<FullscreenViewportStoredState>;
      if (!parsed || typeof parsed !== 'object') return null;

      const views: Record<string, FullscreenViewportStoredViewState> = {};
      for (const [viewId, viewState] of Object.entries(parsed.views ?? {})) {
        if (!viewState || typeof viewState !== 'object') continue;

        const state = viewState as Partial<FullscreenViewportStoredViewState>;
        const targets = sanitizeViewportTargets(state.targets);
        if (Object.keys(targets).length === 0) continue;

        views[viewId] = {
          ...(typeof state.target === 'string' ? { target: state.target } : {}),
          targets,
          updatedAt:
            typeof state.updatedAt === 'number' &&
            Number.isFinite(state.updatedAt)
              ? state.updatedAt
              : 0,
        };
      }
      const activeViewId =
        typeof parsed.activeViewId === 'string' ? parsed.activeViewId : null;
      if (Object.keys(views).length === 0 && !activeViewId) return null;

      return {
        activeViewId,
        updatedAt:
          typeof parsed.updatedAt === 'number' &&
          Number.isFinite(parsed.updatedAt)
            ? parsed.updatedAt
            : 0,
        views,
      };
    } catch {
      return null;
    }
  }

  private _writeFullscreenViewportTargets(
    targets: Record<string, CanvasViewportTargetPosition>,
    viewId: string | null = this._resolveCurrentFullscreenViewportViewId(
      this.doc.getBlock(this.databaseId)?.model
    ),
    options: { replaceTargets?: boolean } = {}
  ) {
    const key = this._fullscreenViewportStorageKey();
    if (!key || !viewId || typeof localStorage === 'undefined') return;

    try {
      const previous = this._readFullscreenViewportState();
      const previousView = previous?.views[viewId];
      const nextTargets = options.replaceTargets
        ? targets
        : {
            ...(previousView?.targets ?? {}),
            ...targets,
          };
      const updatedAt = Date.now();
      const next: FullscreenViewportStoredState = {
        activeViewId: viewId,
        updatedAt,
        views: {
          ...(previous?.views ?? {}),
          [viewId]: {
            target:
              (this._lastFullscreenViewportTargetId &&
              this._lastFullscreenViewportTargetId in nextTargets
                ? this._lastFullscreenViewportTargetId
                : undefined) ??
              (previousView?.target && previousView.target in nextTargets
                ? previousView.target
                : undefined) ??
              Object.keys(nextTargets).find(
                targetId => targetId in nextTargets
              ),
            targets: nextTargets,
            updatedAt,
          },
        },
      };
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Ignore private-mode/quota failures; fullscreen still works without restore.
    }
  }

  private _writeFullscreenActiveView(viewId: string): boolean {
    const key = this._fullscreenViewportStorageKey();
    if (!key || typeof localStorage === 'undefined') return false;

    try {
      const previous = this._readFullscreenViewportState();
      if (previous?.activeViewId === viewId) return false;
      const updatedAt = Date.now();
      const next: FullscreenViewportStoredState = {
        activeViewId: viewId,
        updatedAt,
        views: previous?.views ?? {},
      };
      localStorage.setItem(key, JSON.stringify(next));
      return true;
    } catch {
      // Browser storage is best-effort local UI state.
      return false;
    }
  }

  private _readStoredFullscreenActiveViewId(): string | null {
    return this._readFullscreenViewportState()?.activeViewId ?? null;
  }

  private _readFullscreenViewportRestoreState(): FullscreenViewportRestoreState | null {
    if (this.focusRowId) return null;

    const key = this._fullscreenViewportStorageKey();
    if (!key || this._fullscreenViewportRestoreBlockedKey === key) return null;

    const stored = this._readFullscreenViewportState(key);
    if (!stored) return null;

    const currentViewId = this._resolveCurrentFullscreenViewportViewId(
      this.doc.getBlock(this.databaseId)?.model
    );
    const viewId = this.viewId ?? stored.activeViewId ?? currentViewId;
    if (!viewId) return null;

    const viewState = stored.views[viewId];
    if (!viewState || Object.keys(viewState.targets).length === 0) return null;

    return {
      ...viewState,
      gateKey: `${key}:${encodeURIComponent(viewId)}`,
      storageKey: key,
      viewId,
    };
  }

  private _setFullscreenViewportRestorePending(value: boolean) {
    if (this._fullscreenViewportRestorePending === value) return;

    this._fullscreenViewportRestorePending = value;
  }

  private _settleFullscreenViewportRestoreGate(
    state: FullscreenViewportRestoreState | null
  ) {
    this._activeFullscreenViewportRestoreState = null;
    if (state) {
      this._fullscreenViewportRestoreSettledKey = state.gateKey;
    }
    this._setFullscreenViewportRestorePending(false);
  }

  private _queueFullscreenViewportPersist(
    targetId: string,
    position: CanvasViewportTargetPosition
  ) {
    if (!this._persistViewportState) return;

    this._pendingFullscreenViewportTargets[targetId] = position;
    if (this._fullscreenViewportPersistTimer !== null) {
      clearTimeout(this._fullscreenViewportPersistTimer);
    }
    this._fullscreenViewportPersistTimer = setTimeout(() => {
      this._fullscreenViewportPersistTimer = null;
      this._flushPendingFullscreenViewportPersist();
    }, DATABASE_FULLSCREEN_VIEWPORT_PERSIST_DELAY_MS);
  }

  private _flushPendingFullscreenViewportPersist() {
    if (!this._persistViewportState) {
      this._pendingFullscreenViewportTargets = {};
      return;
    }

    const targets = this._pendingFullscreenViewportTargets;
    this._pendingFullscreenViewportTargets = {};
    if (Object.keys(targets).length === 0) return;

    this._writeFullscreenViewportTargets(targets);
  }

  private _readCurrentFullscreenViewportTargets(): Record<
    string,
    CanvasViewportTargetPosition
  > {
    return Object.fromEntries(
      this._collectFullscreenViewportParticipants()
        .flatMap(participant =>
          Object.entries(participant.getCanvasViewportTargets?.() ?? {})
        )
        .map(([targetId, position]) => [
          targetId,
          sanitizeViewportPosition(position),
        ])
        .filter(
          (entry): entry is [string, CanvasViewportTargetPosition] =>
            typeof entry[0] === 'string' && entry[1] != null
        )
    );
  }

  private _persistCurrentFullscreenViewportTargets() {
    if (!this._persistViewportState) return;

    const targets = this._readCurrentFullscreenViewportTargets();
    if (Object.keys(targets).length === 0) return;

    this._writeFullscreenViewportTargets(targets, undefined, {
      replaceTargets: true,
    });
  }

  private _collectFullscreenViewportParticipants(): FullscreenViewportParticipant[] {
    const viewport = this._viewportRef.value;
    if (!viewport) return [];

    return [
      ...viewport.querySelectorAll<FullscreenViewportParticipant>(
        DATABASE_FULLSCREEN_VIEWPORT_PARTICIPANT_SELECTOR
      ),
    ].filter(
      participant =>
        typeof participant.getCanvasViewportTargets === 'function' ||
        typeof participant.applyCanvasViewportTarget === 'function'
    );
  }

  private _scheduleFullscreenViewportRestore(
    attempt: number,
    restoreState = this._readFullscreenViewportRestoreState()
  ) {
    if (!this._persistViewportState) {
      this._settleFullscreenViewportRestoreGate(restoreState);
      return;
    }

    if (this.focusRowId) {
      this._fullscreenViewportRestoreBlockedKey =
        this._fullscreenViewportStorageKey();
      this._settleFullscreenViewportRestoreGate(restoreState);
      return;
    }

    const key =
      restoreState?.storageKey ?? this._fullscreenViewportStorageKey();
    if (!key || this._fullscreenViewportRestoreBlockedKey === key) {
      this._settleFullscreenViewportRestoreGate(restoreState);
      return;
    }
    if (this._fullscreenViewportRestoreFrame !== null) {
      cancelAnimationFrame(this._fullscreenViewportRestoreFrame);
      this._fullscreenViewportRestoreFrame = null;
    }

    if (
      !restoreState ||
      this._fullscreenViewportRestoreSettledKey === restoreState.gateKey
    ) {
      this._settleFullscreenViewportRestoreGate(restoreState);
      return;
    }

    this._activeFullscreenViewportRestoreState = restoreState;
    this._fullscreenViewportRestoreFrame = requestAnimationFrame(() => {
      this._fullscreenViewportRestoreFrame = null;
      const participants = this._collectFullscreenViewportParticipants();
      if (participants.length === 0) {
        if (attempt < DATABASE_FULLSCREEN_VIEWPORT_RESTORE_MAX_ATTEMPTS) {
          this._scheduleFullscreenViewportRestore(attempt + 1, restoreState);
        } else {
          this._settleFullscreenViewportRestoreGate(restoreState);
        }
        return;
      }

      const pendingTargetIds = new Set(Object.keys(restoreState.targets));
      const matchedTargetIds = new Set<string>();
      const matchedUnrestoredTargetIds = new Set<string>();
      // A local scroll whose save still waits for its debounce is newer than
      // any stored position of that target.
      const unsavedLocalTargets = this._pendingFullscreenViewportTargets;
      participants.forEach(participant => {
        const liveTargets = participant.getCanvasViewportTargets?.() ?? {};
        Object.entries(restoreState.targets).forEach(([targetId, position]) => {
          if (!(targetId in liveTargets)) return;
          matchedTargetIds.add(targetId);
          pendingTargetIds.delete(targetId);
          if (targetId in unsavedLocalTargets) return;
          let restored = isFullscreenViewportTargetRestored(
            position,
            liveTargets[targetId]
          );
          if (
            !restored &&
            typeof participant.applyCanvasViewportTarget === 'function'
          ) {
            participant.applyCanvasViewportTarget(targetId, position);
            const restoredTargets =
              participant.getCanvasViewportTargets?.() ?? {};
            restored = isFullscreenViewportTargetRestored(
              position,
              restoredTargets[targetId]
            );
          }
          if (!restored) {
            matchedUnrestoredTargetIds.add(targetId);
          }
        });
      });

      // Some views (for example Calendar today) do not expose fullscreen
      // viewport participants, and older local state may still contain stale
      // targets for them. Keep the UI visible while retrying those optional
      // restores; hiding the whole database turns a harmless stale restore into
      // a visible blank load.
      if (matchedTargetIds.size === 0) {
        if (attempt < DATABASE_FULLSCREEN_VIEWPORT_RESTORE_MAX_ATTEMPTS) {
          this._scheduleFullscreenViewportRestore(attempt + 1, restoreState);
          return;
        }
        this._settleFullscreenViewportRestoreGate(restoreState);
        return;
      }

      // Stored viewport state can contain stale/cross-view target ids from
      // older builds or from view transitions. Once every live matched target
      // is restored, those unmatched ids must not keep the fullscreen surface
      // hidden while the editor retries a target that cannot appear here.
      if (matchedUnrestoredTargetIds.size === 0) {
        if (pendingTargetIds.size > 0) {
          this._persistCurrentFullscreenViewportTargets();
        }
        this._settleFullscreenViewportRestoreGate(restoreState);
        return;
      }

      this._setFullscreenViewportRestorePending(true);
      if (attempt < DATABASE_FULLSCREEN_VIEWPORT_RESTORE_MAX_ATTEMPTS) {
        this._scheduleFullscreenViewportRestore(attempt + 1, restoreState);
        return;
      }
      this._settleFullscreenViewportRestoreGate(restoreState);
    });
  }

  private readonly _handleFullscreenViewportParticipantReady = () => {
    this._scheduleFullscreenViewportRestore(0);
  };

  private readonly _handleFullscreenViewportTargetScroll = (event: Event) => {
    const detail = (
      event as CustomEvent<{
        position?: unknown;
        targetId?: unknown;
      }>
    ).detail;
    const targetId =
      typeof detail?.targetId === 'string' ? detail.targetId : null;
    const position = sanitizeViewportPosition(detail?.position);
    if (!targetId || !position) return;

    // Participant scroll notifications represent a newer local intent (restore
    // applies suppress their own events). Never replay the older stored position
    // on the next frame, or when the same participant announces readiness again.
    const restoreState = this._activeFullscreenViewportRestoreState;
    if (restoreState) {
      if (this._fullscreenViewportRestoreFrame !== null) {
        cancelAnimationFrame(this._fullscreenViewportRestoreFrame);
        this._fullscreenViewportRestoreFrame = null;
      }
      this._settleFullscreenViewportRestoreGate(restoreState);
    }
    this._lastFullscreenViewportTargetId = targetId;
    this._queueFullscreenViewportPersist(targetId, position);
  };

  private _applyFocusCellRequest() {
    if (this._focusCellFrame !== null) {
      cancelAnimationFrame(this._focusCellFrame);
      this._focusCellFrame = null;
    }
    if (this.focusRowId) {
      this._fullscreenViewportRestoreBlockedKey =
        this._fullscreenViewportStorageKey();
    }
    if (!this.focusRowId) {
      this._lastConsumedFocusCellKey = null;
      return;
    }
    this._scheduleFocusCellRequest(0);
  }

  private _focusCellKey(position: FocusCellPosition) {
    return `${this.databaseId}:${position.rowId}:${position.columnId ?? ''}`;
  }

  private _dispatchFocusCellConsumed(position: FocusCellPosition) {
    const key = this._focusCellKey(position);
    if (this._lastConsumedFocusCellKey === key) return;
    this._lastConsumedFocusCellKey = key;
    this.dispatchEvent(
      new CustomEvent('database-focus-consumed', {
        bubbles: true,
        composed: true,
        detail: {
          columnId: position.columnId,
          rowId: position.rowId,
        },
      })
    );
  }

  private _resolveFocusCellPosition(): FocusCellPosition | null {
    if (!this.focusRowId) return null;

    try {
      // Focus must mutate the DataSource that owns the visible fullscreen
      // table. A newly constructed DataSource has its own transient row
      // window, so changing its offset never paginates the rendered table.
      const tableLogic = this._findVirtualTableUi()?.logic;
      const dataSource = tableLogic?.root?.config?.dataSource;
      const view = tableLogic?.view;
      const viewId = view?.id;
      if (!dataSource || !view || !viewId) return null;

      const columnId = this.focusColumnId;
      const properties = view.getBoundedPropertiesWindow().properties;
      const rowWindow = view.getBoundedRowsWindow();
      const groupPosition = this._findFocusCellGroupPosition(this.focusRowId);
      let rowIndex =
        groupPosition?.rowIndex ?? rowWindow.rowIds.indexOf(this.focusRowId);
      if (rowIndex < 0 && !groupPosition) {
        if (!this.focusRowShardDocId) {
          throw new Error(
            `Database fullscreen focus is missing its row shard locator: database=${this.databaseId} row=${this.focusRowId}`
          );
        }
        if (!(dataSource instanceof DatabaseBlockDataSource)) return null;
        const absoluteRowIndex = dataSource.resolveRowShardAbsoluteIndex(
          this.focusRowId,
          this.focusRowShardDocId
        );
        const targetWindow =
          absoluteRowIndex === null
            ? null
            : {
                targetOffset:
                  Math.floor(absoluteRowIndex / view.rowWindowLimit$.value) *
                  view.rowWindowLimit$.value,
                rowIndex: absoluteRowIndex % view.rowWindowLimit$.value,
              };
        if (targetWindow) {
          if (view.rowWindowOffset$.value !== targetWindow.targetOffset) {
            view.setRowWindowOffset(targetWindow.targetOffset);
            tableLogic.root?.dataViewRenderer?.requestUpdate();
            return null;
          }
          rowIndex = targetWindow.rowIndex;
        }
      }
      const fallbackColumnIndex = Math.max(
        0,
        properties.findIndex(property => property.type$.value === 'title')
      );
      const columnIndex = columnId
        ? properties.findIndex(property => property.id === columnId)
        : fallbackColumnIndex;

      if (rowIndex < 0 || columnIndex < 0) return null;

      return {
        columnId,
        columnIndex,
        groupKey: groupPosition?.groupKey,
        rowId: this.focusRowId,
        rowIndex,
        viewId,
      };
    } catch {
      return null;
    }
  }

  private _applyFocusCellSelection(position: FocusCellPosition) {
    try {
      this.std.selection.setGroup('note', [
        new DatabaseSelection({
          blockId: this.databaseId,
          surface: 'fullscreen',
          viewSelection: {
            selectionType: 'area',
            groupKey: position.groupKey,
            rowsSelection: {
              start: position.rowIndex,
              end: position.rowIndex,
            },
            columnsSelection: {
              start: position.columnIndex,
              end: position.columnIndex,
            },
            focus: {
              rowIndex: position.rowIndex,
              columnIndex: position.columnIndex,
            },
            isEditing: false,
            type: 'table',
            viewId: position.viewId,
          },
        }),
      ]);
    } catch {
      // Search navigation should still open the database if row focus cannot be applied.
    }
  }

  private _findVirtualTableUi():
    | (HTMLElement & {
        logic?: {
          root?: {
            config?: {
              dataSource?: DataSource;
            };
            dataViewRenderer?: {
              requestUpdate: () => void;
            };
          };
          view?: {
            getBoundedPropertiesWindow: () => {
              properties: Array<{
                id: string;
                ['type$']: { value: string };
              }>;
            };
            getBoundedRowsWindow: () => { rowIds: string[] };
            id: string;
            ['rowWindowLimit$']: { value: number };
            ['rowWindowOffset$']: { value: number };
            setRowWindowOffset: (offset: number) => void;
          };
          selectionController?: {
            selection?: unknown;
          };
          ['virtualScroll$']?: {
            value?: {
              ['columnPosition$']?: {
                value?: { left: number; width: number }[];
              };
              container?: {
                ['viewport$']?: { value?: { height: number; width: number } };
              };
              getGroup?: (groupKey: string) => {
                getRowHeight?: (rowId: string) => number;
                getRowTop?: (rowId: string) => number | undefined;
              };
              scrollToPosition?: (x: number, y: number) => void;
            };
          };
        };
      })
    | null {
    return (
      this._viewportRef.value?.querySelector('dv-table-view-ui-virtual') ?? null
    );
  }

  private _findFocusCellGroupPosition(
    rowId: string
  ): { groupKey?: string; rowIndex: number } | null {
    const logic = this._findVirtualTableUi()?.logic;
    const groups = (
      logic as
        | {
            ['groups$']?: {
              value?: Array<{ id: string; rows: readonly string[] }>;
            };
          }
        | undefined
    )?.['groups$']?.value;
    const group = groups?.find(group => group.rows.includes(rowId));
    if (!group) return null;
    return {
      groupKey: group.id || undefined,
      rowIndex: group.rows.indexOf(rowId),
    };
  }

  private _findFocusedCellElement(
    rowId: string,
    columnId: string | null
  ): HTMLElement | null {
    if (!columnId) return null;
    const viewport = this._viewportRef.value;
    if (!viewport) return null;
    const escapedRowId = escapeAttributeValue(rowId);
    const escapedColumnId = escapeAttributeValue(columnId);
    return viewport.querySelector<HTMLElement>(
      `[data-database-cell-frame="true"][data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"],` +
        `affine-database-virtual-cell-container[data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"],` +
        `dv-table-view-cell-container[data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"]`
    );
  }

  private _findScrollableAncestor(
    element: HTMLElement,
    axis: 'x' | 'y'
  ): HTMLElement | null {
    let current: Element | null = element.parentElement;

    while (current instanceof HTMLElement) {
      const styles = getComputedStyle(current);
      const overflow = axis === 'x' ? styles.overflowX : styles.overflowY;

      if (overflow === 'auto' || overflow === 'scroll') {
        return current;
      }

      const root = current.getRootNode();
      current =
        current.parentElement ??
        (root instanceof ShadowRoot && root.host instanceof HTMLElement
          ? root.host
          : null);
    }

    return null;
  }

  private _getStickyHeaderHeight(table: HTMLElement): number {
    return (
      table
        .querySelector<HTMLElement>('virtual-table-header')
        ?.getBoundingClientRect().height ?? 0
    );
  }

  private _getSelectableFocusedCell(
    cellElement: HTMLElement
  ): (HTMLElement & { selectCurrentCell?: (editing: boolean) => void }) | null {
    const selector =
      'affine-database-virtual-cell-container,dv-table-view-cell-container';
    if (cellElement.matches(selector)) {
      return cellElement as HTMLElement & {
        selectCurrentCell?: (editing: boolean) => void;
      };
    }
    return cellElement.querySelector(selector);
  }

  private _alignFocusedCellElement(cellElement: HTMLElement): boolean {
    const table = this._findVirtualTableUi();
    const target =
      cellElement.closest<HTMLElement>('[data-database-cell-frame="true"]') ??
      cellElement;
    const yScrollContainer = this._findScrollableAncestor(target, 'y');
    const xScrollContainer = this._findScrollableAncestor(target, 'x');
    let aligned = false;

    if (yScrollContainer) {
      const targetRect = target.getBoundingClientRect();
      const containerRect = yScrollContainer.getBoundingClientRect();
      const headerHeight = table ? this._getStickyHeaderHeight(table) : 0;
      const deltaY = targetRect.top - containerRect.top - headerHeight;

      if (Math.abs(deltaY) > 1) {
        yScrollContainer.scrollTop = Math.max(
          0,
          yScrollContainer.scrollTop + deltaY
        );
      }
      aligned = true;
    }

    if (xScrollContainer) {
      const targetRect = target.getBoundingClientRect();
      const containerRect = xScrollContainer.getBoundingClientRect();
      const leftDelta = targetRect.left - containerRect.left;
      const rightDelta = targetRect.right - containerRect.right;

      if (leftDelta < 0) {
        xScrollContainer.scrollLeft = Math.max(
          0,
          xScrollContainer.scrollLeft + leftDelta
        );
      } else if (rightDelta > 0) {
        xScrollContainer.scrollLeft += rightDelta;
      }
      aligned = true;
    }

    return aligned;
  }

  private _scrollFocusedCellPosition(position: FocusCellPosition): boolean {
    const table = this._findVirtualTableUi();
    const virtualScroll = table?.logic?.['virtualScroll$']?.value;
    const group = virtualScroll?.getGroup?.(position.groupKey ?? '');
    const rowTop = group?.getRowTop?.(position.rowId);
    if (rowTop == null) return false;

    const viewport = virtualScroll?.container?.['viewport$']?.value;
    const columnPosition =
      virtualScroll?.['columnPosition$']?.value?.[position.columnIndex + 1];
    const x =
      columnPosition && viewport
        ? Math.max(
            0,
            columnPosition.left - viewport.width / 2 + columnPosition.width / 2
          )
        : 0;
    const y = Math.max(0, rowTop);

    virtualScroll?.scrollToPosition?.(x, y);
    return true;
  }

  private _activateFocusedCellElement(position: FocusCellPosition): boolean {
    const cellElement = this._findFocusedCellElement(
      position.rowId,
      position.columnId
    );
    if (!cellElement) return false;

    this._getSelectableFocusedCell(cellElement)?.selectCurrentCell?.(false);
    this._alignFocusedCellElement(cellElement);
    return true;
  }

  private _scheduleFocusCellRequest(attempt: number) {
    this._focusCellFrame = requestAnimationFrame(() => {
      this._focusCellFrame = null;
      const position = this._resolveFocusCellPosition();
      if (!position) {
        if (attempt < DATABASE_FULLSCREEN_FOCUS_MAX_ATTEMPTS) {
          this._scheduleFocusCellRequest(attempt + 1);
        }
        return;
      }

      this._applyFocusCellSelection(position);
      this._scrollFocusedCellPosition(position);
      const activated = this._activateFocusedCellElement(position);

      if (activated) {
        this._dispatchFocusCellConsumed(position);
      }

      if (!activated && attempt < DATABASE_FULLSCREEN_FOCUS_MAX_ATTEMPTS) {
        this._scheduleFocusCellRequest(attempt + 1);
      }
    });
  }

  private _formatSelectedRowsFromView(rowIds: string[], model: any) {
    try {
      const dataSource = createDatabaseBlockDataSource(model);
      const view = dataSource.viewManager.currentView$.value;
      const rowWindow = view?.getBoundedRowIdsWindow();
      if (!rowWindow) {
        return `${rowIds.length} selected rows`;
      }

      const visibleIndices = rowIds
        .map(id => rowWindow.rowIds.indexOf(id))
        .filter(index => index >= 0)
        .map(index => rowWindow.offset + index);

      if (visibleIndices.length !== rowIds.length) {
        return `${rowIds.length} selected rows`;
      }

      return formatRowLabel(visibleIndices);
    } catch {
      return `${rowIds.length} selected rows`;
    }
  }

  private _dispatchRowSelectionEvent(
    selectedRowIds: string[],
    selectedRowLabel: string,
    visibleRowWindow?: ReturnType<typeof getVisibleRowWindowFromModel>
  ) {
    if (!this._emitFullscreenEvents) return;

    window.dispatchEvent(
      new CustomEvent('streamlain:fullscreen-row-selection', {
        detail: {
          databaseId: this.databaseId,
          selectedRowIds,
          selectedRowLabel,
          visibleRowWindow,
        },
      })
    );
  }

  // sl-wo7: Handle rename event from React context menu
  private _handleRenameEvent(event: Event) {
    const customEvent = event as CustomEvent<{ tabId: string }>;
    const { tabId } = customEvent.detail;
    this.startRenameTab(tabId);
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete();
    await this.host?.updateComplete;
    // Start observing for database elements and filter them
    this._setupDatabaseFiltering();
    return result;
  }

  /**
   * Set up filtering to show only the target database.
   * Uses MutationObserver to handle dynamically added elements.
   */
  private _setupDatabaseFiltering() {
    // Initial filter
    this._filterDatabases();

    // Set up observer for dynamic content
    if (!this._observer) {
      this._observer = new MutationObserver(() => {
        this._filterDatabases();
      });

      // Observe the entire component for changes
      this._observer.observe(this, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Show only the target database, hide all others.
   * The target database may have hidden itself due to page-mode filtering,
   * so we explicitly make it visible.
   */
  private _filterDatabases() {
    // Query all affine-database elements
    const databases = this.querySelectorAll('affine-database');

    databases.forEach(db => {
      const blockId =
        db.getAttribute('data-block-id') ??
        (db as { model?: { id?: string } }).model?.id ??
        null;
      const element = db as HTMLElement;

      if (!blockId) {
        return;
      }

      const willShow = blockId === this.databaseId;

      if (willShow) {
        // SHOW the target database (override any page-mode hide)
        element.style.display = 'flex';
      } else {
        // HIDE other databases
        element.style.display = 'none';
      }
    });
  }

  private _applyRequestedView() {
    if (!this.doc || !this.databaseId) return;
    const databaseBlock = this.doc.getBlock(this.databaseId);
    if (!databaseBlock) return;
    const model = databaseBlock.model as any;
    if (!isDatabaseStorageModelReady(model)) {
      this._observeRequestedViewStorageReady(model);
      return;
    }

    this._teardownRequestedViewStorageReadyObserver();
    const dataSource = createDatabaseBlockDataSource(model);
    dataSource.setUiStatePersistence('local');
    const requestedViewId =
      this.viewId ?? this._readStoredFullscreenActiveViewId();
    if (!requestedViewId) return;
    if (!dataSource.viewManager.views$.value.includes(requestedViewId)) {
      this.dispatchEvent(
        new CustomEvent('database-view-unavailable', {
          bubbles: true,
          composed: true,
          detail: { viewId: requestedViewId },
        })
      );
      return;
    }
    if (dataSource.viewManager.currentViewId$.value !== requestedViewId) {
      dataSource.viewManager.setCurrentView(requestedViewId);
    }
    this._writeFullscreenActiveView(requestedViewId);
    if (!this.viewId) {
      window.dispatchEvent(
        new CustomEvent('streamlain:database-view-change', {
          detail: {
            blockId: this.databaseId,
            viewId: requestedViewId,
          },
        })
      );
    }
  }

  private _observeRequestedViewStorageReady(model: any) {
    const key = `${this.doc.id}:${this.databaseId}:${
      this.viewId ?? this._readStoredFullscreenActiveViewId() ?? ''
    }`;
    if (this._requestedViewStorageReadyKey === key) {
      return;
    }

    this._teardownRequestedViewStorageReadyObserver();
    this._requestedViewStorageReadyKey = key;
    const cleanup = observeDatabaseStorageModelReady(model, () => {
      this._teardownRequestedViewStorageReadyObserver();
      this._applyRequestedView();
      this._updateDatabaseTabs();
      this.requestUpdate();
    });
    if (isDatabaseStorageModelReady(model)) {
      cleanup();
      this._requestedViewStorageReadyKey = null;
      return;
    }
    this._requestedViewStorageReadyCleanup = cleanup;
  }

  private _getDatabaseTabTitle(
    model: any
  ): Pick<DatabaseTabInfo, 'title' | 'titleLoading'> {
    if (!isDatabaseStorageModelReady(model)) {
      this._observeRequestedViewStorageReady(model);
      return {
        title: DATABASE_TAB_TITLE_LOADING_LABEL,
        titleLoading: true,
      };
    }
    const dataSource = createDatabaseBlockDataSource(model);
    const titleText = dataSource.storageModel.props.title;
    this._observeTabTitle(model.id, titleText);
    const title = titleText.toString().trim();
    return {
      title: title || 'Untitled',
      titleLoading: false,
    };
  }

  private _observeTabTitle(databaseId: string, titleText: { yText?: YText }) {
    const yText = titleText.yText;
    const current = this._tabTitleObservers.get(databaseId);
    if (current?.yText === yText) return;
    current?.cleanup();
    this._tabTitleObservers.delete(databaseId);
    if (!yText) return;
    const refresh = () => this._updateDatabaseTabs();
    yText.observe(refresh);
    this._tabTitleObservers.set(databaseId, {
      yText,
      cleanup: () => yText.unobserve(refresh),
    });
  }

  private _teardownRequestedViewStorageReadyObserver() {
    this._requestedViewStorageReadyCleanup?.();
    this._requestedViewStorageReadyCleanup = null;
    this._requestedViewStorageReadyKey = null;
  }

  private readonly _handleDatabaseViewChange = (event: Event) => {
    if (!this._showViewBar) return;

    const detail = (event as CustomEvent<{ blockId?: string; viewId?: string }>)
      .detail;
    if (
      detail?.blockId !== this.databaseId ||
      !detail.viewId ||
      this.viewId === detail.viewId
    ) {
      return;
    }

    this._flushPendingFullscreenViewportPersist();
    this._persistCurrentFullscreenViewportTargets();
    this._writeFullscreenActiveView(detail.viewId);
    this.viewId = detail.viewId;
  };

  /**
   * E022-SPK023: Find all database blocks in the current doc
   */
  private _updateDatabaseTabs() {
    const tabs: DatabaseTabInfo[] = listUserDatabaseBlocks(this.doc).map(
      ({ id, model }) => ({ id, ...this._getDatabaseTabTitle(model) })
    );
    const tabIds = new Set(tabs.map(tab => tab.id));
    for (const [databaseId, observer] of this._tabTitleObservers) {
      if (tabIds.has(databaseId)) continue;
      observer.cleanup();
      this._tabTitleObservers.delete(databaseId);
    }

    // Apply saved tab order from docMeta
    if (this.tabOrder.length > 0) {
      // Separate known and new tabs
      const knownTabs: DatabaseTabInfo[] = [];
      const newTabs: DatabaseTabInfo[] = [];

      for (const tab of tabs) {
        if (this.tabOrder.includes(tab.id)) {
          knownTabs.push(tab);
        } else {
          newTabs.push(tab);
        }
      }

      // Sort known tabs by saved order
      knownTabs.sort((a, b) => {
        const indexA = this.tabOrder.indexOf(a.id);
        const indexB = this.tabOrder.indexOf(b.id);
        return indexA - indexB;
      });

      // New tabs go at the beginning (newest first)
      newTabs.reverse();
      this._databaseTabs = [
        ...this._orderCanonicalDuplicateTabs(newTabs),
        ...knownTabs,
      ];
    } else {
      // No saved order - reverse so newest databases appear first
      tabs.reverse();
      this._databaseTabs = this._orderCanonicalDuplicateTabs(tabs);
    }

    // Auto-switch when current database was deleted
    if (
      this.doc.root &&
      this.databaseId !== '__placeholder__' &&
      !this._databaseTabs.find(t => t.id === this.databaseId)
    ) {
      const nextId =
        this._databaseTabs.length > 0
          ? this._databaseTabs[0].id
          : '__placeholder__';
      queueMicrotask(() => {
        this.dispatchEvent(
          new CustomEvent('database-tab-switch', {
            detail: { databaseId: nextId },
            bubbles: true,
            composed: true,
          })
        );
      });
    }
  }

  /**
   * E022-SPK023: Handle tab click to switch database
   */
  private _onTabClick(tabId: string) {
    if (tabId !== this.databaseId) {
      // Dispatch event to switch database
      this.dispatchEvent(
        new CustomEvent('database-tab-switch', {
          detail: { databaseId: tabId },
          bubbles: true,
          composed: true,
        })
      );

      // sl-57s.1.2: Notify AI context about tab switch
      this._dispatchFullscreenContextEvent('tab-switch', tabId);
    }
  }

  /**
   * E022-SPK023: Handle tab double-click to rename database
   */
  private _onTabDoubleClick(e: MouseEvent, tabId: string) {
    e.stopPropagation();
    const tab = this._databaseTabs.find(t => t.id === tabId);
    if (!tab || tab.titleLoading) return;

    this._editingTabId = tabId;
    this._editingTabTitle = tab.title;
    this.requestUpdate();

    // Focus the input after render
    requestAnimationFrame(() => {
      const input = this.querySelector(
        `input[data-tab-id="${tabId}"]`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  /**
   * E022-SPK023: Handle tab title input change
   */
  private _onTabTitleInput(e: InputEvent) {
    const input = e.target as HTMLInputElement;
    this._editingTabTitle = input.value;
  }

  /**
   * E022-SPK023: Handle tab title input blur (save changes)
   */
  private _onTabTitleBlur(tabId: string) {
    this._saveTabTitle(tabId);
  }

  /**
   * E022-SPK023: Handle tab title input keydown (Enter to save, Escape to cancel)
   */
  private _onTabTitleKeydown(e: KeyboardEvent, tabId: string) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this._saveTabTitle(tabId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._cancelTabEdit();
    }
  }

  /**
   * E022-SPK023: Save the edited tab title to the database block
   */
  private _saveTabTitle(tabId: string) {
    if (!this._editingTabId) return;

    const newTitle = this._editingTabTitle.trim();
    try {
      const block = newTitle ? this.doc.getBlock(tabId) : null;
      if (block) {
        // The data source writes the title as a local publication, like the
        // title in the block header; a direct store transaction is refused
        // on a managed database document.
        createDatabaseBlockDataSource(block.model as any).titleSet(newTitle);
        this._updateDatabaseTabs();
      }
    } finally {
      this._cancelTabEdit();
    }
  }

  /**
   * E022-SPK023: Cancel tab editing
   */
  private _cancelTabEdit() {
    this._editingTabId = null;
    this._editingTabTitle = '';
    this.requestUpdate();
  }

  private _orderCanonicalDuplicateTabs(tabs: DatabaseTabInfo[]) {
    const ordered = orderCanonicalDatabaseCandidates(
      tabs.map(tab => ({
        ...tab,
        model: this.doc.getBlock(tab.id)?.model,
      }))
    );
    const seen = new Set<string>();
    return ordered
      .filter(tab => {
        const key = getDatabaseCanonicalReferenceKey(tab);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ model: _model, ...tab }) => tab);
  }

  /**
   * E022-SPK023: Create a new database on the canvas
   */
  private _onAddDatabase() {
    // Dispatch event to create new database
    this.dispatchEvent(
      new CustomEvent('database-tab-add', {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * sl-wo7: Handle tab context menu (right-click)
   * Dispatches event with tab info and position for React to render the menu
   */
  private _onTabContextMenu(e: MouseEvent, tabId: string) {
    e.preventDefault();
    e.stopPropagation();

    const tab = this._databaseTabs.find(t => t.id === tabId);
    if (!tab) return;

    console.log('[sl-wo7] Tab context menu triggered:', {
      tabId,
      tabTitle: tab.title,
      titleLoading: tab.titleLoading,
      x: e.clientX,
      y: e.clientY,
    });

    // Dispatch event for React wrapper to show context menu
    window.dispatchEvent(
      new CustomEvent('streamlain:database-tab-context-menu', {
        detail: {
          tabId,
          tabTitle: tab.title,
          titleLoading: tab.titleLoading,
          x: e.clientX,
          y: e.clientY,
        },
      })
    );
  }

  /**
   * sl-wo7: Start rename mode for a tab (called from context menu)
   */
  startRenameTab(tabId: string) {
    const tab = this._databaseTabs.find(t => t.id === tabId);
    if (!tab || tab.titleLoading) return;

    this._editingTabId = tabId;
    this._editingTabTitle = tab.title;
    this.requestUpdate();

    // Focus the input after render
    requestAnimationFrame(() => {
      const input = this.querySelector(
        `input[data-tab-id="${tabId}"]`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  /**
   * sl-wo7: Delete a database tab
   */
  deleteTab(tabId: string) {
    // If deleting the current tab, switch to another first
    if (tabId === this.databaseId) {
      const currentIndex = this._databaseTabs.findIndex(t => t.id === tabId);
      const nextTab =
        this._databaseTabs[currentIndex + 1] ||
        this._databaseTabs[currentIndex - 1];
      if (nextTab) {
        this._onTabClick(nextTab.id);
      }
    }

    // Delete the database block
    const block = this.doc.getBlock(tabId);
    if (block) {
      this.std.command.exec(deleteSelectedModelsCommand, {
        selectedModels: [block.model],
      });
    }
  }

  /**
   * E022-SPK023: Drag & Drop handlers for tab reordering
   */
  private _onTabDragStart(e: DragEvent, tabId: string) {
    this._draggedTabId = tabId;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', tabId);
    }
    // Add dragging class after a short delay for visual feedback
    requestAnimationFrame(() => {
      this.requestUpdate();
    });
  }

  private _onTabDragOver(e: DragEvent, tabId: string) {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    if (this._draggedTabId && tabId !== this._draggedTabId) {
      this._dragOverTabId = tabId;
      this.requestUpdate();
    }
  }

  private _onTabDragLeave(_e: DragEvent) {
    this._dragOverTabId = null;
    this.requestUpdate();
  }

  private _onTabDrop(e: DragEvent, targetTabId: string) {
    e.preventDefault();
    if (!this._draggedTabId || this._draggedTabId === targetTabId) {
      this._resetDragState();
      return;
    }

    // Reorder tabs
    const tabs = [...this._databaseTabs];
    const draggedIndex = tabs.findIndex(t => t.id === this._draggedTabId);
    const targetIndex = tabs.findIndex(t => t.id === targetTabId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [draggedTab] = tabs.splice(draggedIndex, 1);
      tabs.splice(targetIndex, 0, draggedTab);
      this._databaseTabs = tabs;

      // Dispatch event for persistence (optional)
      this.dispatchEvent(
        new CustomEvent('database-tabs-reorder', {
          detail: { tabs: tabs.map(t => t.id) },
          bubbles: true,
          composed: true,
        })
      );
    }

    this._resetDragState();
  }

  private _onTabDragEnd(_e: DragEvent) {
    this._resetDragState();
  }

  private _resetDragState() {
    this._draggedTabId = null;
    this._dragOverTabId = null;
    this.requestUpdate();
  }

  /**
   * E022-SPK023: Render the database tabs bar
   */
  private _renderTabsBar() {
    if (!this._showDatabaseTabs) return nothing;

    // Always show tabs bar (even with one database, to allow adding more)
    return html`
      <div class="database-tabs-bar" ${ref(this._tabsBarRef)}>
        <div
          class="database-tab-add"
          title="Add new database"
          @click=${() => this._onAddDatabase()}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path
              d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2z"
            />
          </svg>
        </div>
        ${repeat(
          this._databaseTabs,
          tab => tab.id,
          tab => {
            const isActive = tab.id === this.databaseId;
            const isDragging = tab.id === this._draggedTabId;
            const isDragOver = tab.id === this._dragOverTabId;
            const isEditing = tab.id === this._editingTabId;
            const tabTitleClass = [
              'database-tab-title',
              tab.titleLoading ? 'loading' : '',
            ]
              .filter(Boolean)
              .join(' ');

            const tabClass = [
              'database-tab',
              isActive ? 'active' : '',
              isDragging ? 'dragging' : '',
              isDragOver ? 'drag-over' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return html`
              <div
                class=${tabClass}
                draggable=${!isEditing}
                @click=${() => !isEditing && this._onTabClick(tab.id)}
                @dblclick=${(e: MouseEvent) =>
                  this._onTabDoubleClick(e, tab.id)}
                @contextmenu=${(e: MouseEvent) =>
                  this._onTabContextMenu(e, tab.id)}
                @dragstart=${(e: DragEvent) => this._onTabDragStart(e, tab.id)}
                @dragover=${(e: DragEvent) => this._onTabDragOver(e, tab.id)}
                @dragleave=${(e: DragEvent) => this._onTabDragLeave(e)}
                @drop=${(e: DragEvent) => this._onTabDrop(e, tab.id)}
                @dragend=${(e: DragEvent) => this._onTabDragEnd(e)}
                title=${isEditing || tab.titleLoading ? '' : tab.title}
                aria-busy=${tab.titleLoading ? 'true' : 'false'}
              >
                <svg
                  class="database-tab-icon"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path
                    d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12.5v-9zM3.5 3a.5.5 0 0 0-.5.5V6h10V3.5a.5.5 0 0 0-.5-.5h-9zM13 7H3v5.5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V7z"
                  />
                </svg>
                ${isEditing
                  ? html`<input
                      type="text"
                      class="database-tab-title-input"
                      data-tab-id=${tab.id}
                      .value=${this._editingTabTitle}
                      @input=${(e: InputEvent) => this._onTabTitleInput(e)}
                      @blur=${() => this._onTabTitleBlur(tab.id)}
                      @keydown=${(e: KeyboardEvent) =>
                        this._onTabTitleKeydown(e, tab.id)}
                      @click=${(e: MouseEvent) => e.stopPropagation()}
                    />`
                  : html`<span class=${tabTitleClass}>${tab.title}</span>`}
              </div>
            `;
          }
        )}
      </div>
    `;
  }

  override render() {
    if (!this.doc.root) return nothing;

    // sl-a7z: Show placeholder when no database exists
    if (this.databaseId === '__placeholder__') {
      return html`<div class="database-fullscreen-container">
        <div class="fullscreen-database-placeholder">
          <div class="placeholder-content">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
            <h2>${I18n.t('com.streamlain.databaseFullscreen.empty.title')}</h2>
            <p>${I18n.t('com.streamlain.databaseFullscreen.empty.hint')}</p>
            <button
              class="create-database-btn"
              @click=${() => this._onAddDatabase()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ${I18n.t('com.streamlain.databaseFullscreen.empty.create')}
            </button>
            <p class="hint">
              ${I18n.t(
                'com.streamlain.databaseFullscreen.empty.returnHintPrefix'
              )}
              <kbd>Alt</kbd>+<kbd>E</kbd>
              ${I18n.t(
                'com.streamlain.databaseFullscreen.empty.returnHintSuffix'
              )}
            </p>
          </div>
        </div>
        <style>
          .fullscreen-database-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            min-height: 400px;
            color: var(--streamlain-v2-text-secondary);
          }
          .placeholder-content {
            text-align: center;
            max-width: 400px;
          }
          .placeholder-content svg {
            opacity: 0.5;
            margin-bottom: 16px;
          }
          .placeholder-content h2 {
            margin: 0 0 8px 0;
            font-size: 20px;
            font-weight: 500;
            color: var(--streamlain-v2-text-primary);
          }
          .placeholder-content p {
            margin: 0 0 8px 0;
            font-size: 14px;
          }
          .placeholder-content .create-database-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin: 16px 0;
            padding: 10px 20px;
            background: var(--streamlain-v2-brand-primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }
          .placeholder-content .create-database-btn:hover {
            background: var(--streamlain-v2-brand-primary);
          }
          .placeholder-content .create-database-btn svg {
            opacity: 1;
            margin: 0;
          }
          .placeholder-content .sub-hint {
            margin: 0 0 8px 0;
            font-size: 12px;
            opacity: 0.6;
          }
          .placeholder-content .hint {
            margin-top: 16px;
            font-size: 12px;
            opacity: 0.7;
          }
          .placeholder-content kbd {
            background: var(--streamlain-v2-layer-background-hoverOverlay);
            border-radius: 4px;
            padding: 2px 6px;
            font-family: inherit;
            font-size: 11px;
          }
        </style>
      </div>`;
    }

    // Verify the database exists — if deleted, show tabs bar while auto-switch is in progress
    const databaseBlock = this.doc.getBlock(this.databaseId);
    if (!databaseBlock) {
      return html`
        <div
          class="database-fullscreen-viewport"
          data-database-isolated-surface="true"
          data-database-surface-profile=${this.surfaceProfile}
          data-database-show-title-row=${this._showTitleRow ? 'true' : 'false'}
          data-database-show-toolbar-row=${this._showToolbarRow
            ? 'true'
            : 'false'}
          data-database-show-view-bar=${this._showViewBar ? 'true' : 'false'}
          data-database-show-tools=${this._showTools ? 'true' : 'false'}
          data-database-show-quick-settings=${this._showQuickSettings
            ? 'true'
            : 'false'}
          data-database-show-inline-large-data-boundary=${this
            ._showInlineLargeDataBoundary
            ? 'true'
            : 'false'}
          data-database-show-row-window-footer=${this._showRowWindowFooter
            ? 'true'
            : 'false'}
          data-database-force-row-window-controls=${this._forceRowWindowControls
            ? 'true'
            : 'false'}
        >
          <div class="database-fullscreen-container"></div>
        </div>
        ${this._renderTabsBar()}
      `;
    }

    const std = this.std;

    const viewportClass = [
      'database-fullscreen-viewport',
      this._fullscreenViewportRestorePending ? 'viewport-restoring' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div
        class=${viewportClass}
        data-database-isolated-surface="true"
        data-database-surface-profile=${this.surfaceProfile}
        data-database-show-title-row=${this._showTitleRow ? 'true' : 'false'}
        data-database-show-toolbar-row=${this._showToolbarRow
          ? 'true'
          : 'false'}
        data-database-show-view-bar=${this._showViewBar ? 'true' : 'false'}
        data-database-show-tools=${this._showTools ? 'true' : 'false'}
        data-database-show-quick-settings=${this._showQuickSettings
          ? 'true'
          : 'false'}
        data-database-show-inline-large-data-boundary=${this
          ._showInlineLargeDataBoundary
          ? 'true'
          : 'false'}
        data-database-show-row-window-footer=${this._showRowWindowFooter
          ? 'true'
          : 'false'}
        data-database-force-row-window-controls=${this._forceRowWindowControls
          ? 'true'
          : 'false'}
        data-database-id=${this.databaseId}
        data-focus-row-id=${this.focusRowId ?? ''}
        data-focus-row-shard-doc-id=${this.focusRowShardDocId ?? ''}
        data-focus-column-id=${this.focusColumnId ?? ''}
        aria-busy=${this._fullscreenViewportRestorePending ? 'true' : 'false'}
        ${ref(this._viewportRef)}
      >
        <div class="database-fullscreen-container">
          ${guard([std, this.databaseId], () => std.render())}
        </div>
      </div>
      ${this._renderTabsBar()}
    `;
  }

  override willUpdate(
    changedProperties: Map<string | number | symbol, unknown>
  ) {
    super.willUpdate(changedProperties);
    if (
      this.hasUpdated && // skip the first update
      (changedProperties.has('doc') || changedProperties.has('databaseId'))
    ) {
      this.std = new BlockStdScope({
        store: this.doc,
        extensions: this.specs,
      });

      // Notify AI context chip when database changes (e.g. tab switch via React prop update)
      if (changedProperties.has('databaseId')) {
        this._dispatchFullscreenContextEvent('tab-switch');
      }
    }
    if (
      changedProperties.has('viewId') ||
      changedProperties.has('databaseId')
    ) {
      this._applyRequestedView();
    }
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    const viewportIdentityChanged =
      changedProperties.has('doc') ||
      changedProperties.has('databaseId') ||
      changedProperties.has('viewId') ||
      changedProperties.has('workspaceId') ||
      changedProperties.has('userId');
    if (
      changedProperties.has('focusRowId') ||
      changedProperties.has('focusRowShardDocId') ||
      changedProperties.has('focusColumnId') ||
      changedProperties.has('databaseId') ||
      changedProperties.has('viewId')
    ) {
      this._applyFocusCellRequest();
    }
    if (
      viewportIdentityChanged &&
      !this.focusRowId &&
      this._persistViewportState
    ) {
      this._fullscreenViewportRestoreBlockedKey = null;
      this._scheduleFullscreenViewportRestore(0);
    }
    if (
      changedProperties.has('calendarDetailOnly') ||
      changedProperties.has('calendarDetailOccurrenceStart') ||
      changedProperties.has('calendarDetailRowId') ||
      changedProperties.has('calendarDetailViewId') ||
      changedProperties.has('databaseId') ||
      changedProperties.has('viewId')
    ) {
      const detail: CalendarDetailTarget = {
        detailOnly: this.calendarDetailOnly,
        occurrenceStart: this.calendarDetailOccurrenceStart,
        rowId: this.calendarDetailRowId,
        viewId: this.calendarDetailViewId,
      };
      this.dispatchEvent(
        new CustomEvent(CALENDAR_DETAIL_TARGET_EVENT, {
          detail,
        })
      );
    }
  }

  @property({ attribute: false })
  accessor doc!: Store;

  @property({ attribute: false })
  accessor databaseId!: string;

  @property({ attribute: false })
  accessor viewId: string | null = null;

  @property({ attribute: false })
  accessor focusRowId: string | null = null;

  @property({ attribute: false })
  accessor focusRowShardDocId: string | null = null;

  @property({ attribute: false })
  accessor focusColumnId: string | null = null;

  @property({ attribute: false })
  accessor calendarDetailOnly: boolean = false;

  @property({ attribute: false })
  accessor calendarDetailOccurrenceStart: string | null = null;

  @property({ attribute: false })
  accessor calendarDetailRowId: string | null = null;

  @property({ attribute: false })
  accessor calendarDetailViewId: string | null = null;

  @property({ attribute: false })
  accessor userId: string = '__local__';

  @property({ attribute: false })
  accessor workspaceId: string = '__workspace__';

  @property({ attribute: false })
  accessor specs: ExtensionType[] = [];

  @property({ attribute: false })
  accessor surfaceProfile: DatabaseSurfaceProfile = 'fullscreen';

  @property({ attribute: false })
  accessor showDatabaseTabs: boolean | null = null;

  @property({ attribute: false })
  accessor showTitleRow: boolean | null = null;

  @property({ attribute: false })
  accessor showToolbarRow: boolean | null = null;

  @property({ attribute: false })
  accessor showViewBar: boolean | null = null;

  @property({ attribute: false })
  accessor showTools: boolean | null = null;

  @property({ attribute: false })
  accessor showQuickSettings: boolean | null = null;

  @property({ attribute: false })
  accessor showInlineLargeDataBoundary: boolean | null = null;

  @property({ attribute: false })
  accessor showRowWindowFooter: boolean | null = null;

  @property({ attribute: false })
  accessor forceRowWindowControls: boolean | null = null;

  @property({ attribute: false })
  accessor emitFullscreenEvents: boolean | null = null;

  @property({ attribute: false })
  accessor persistViewportState: boolean | null = null;

  // sl-wo7: Hide tabs bar when rendered externally in React
  @property({ attribute: false })
  accessor hideTabsBar: boolean = false;

  // Tab order from docMeta for persistence
  @property({ attribute: false })
  accessor tabOrder: string[] = [];

  @state()
  accessor std!: BlockStdScope;
}

declare global {
  interface HTMLElementTagNameMap {
    'database-fullscreen-editor': DatabaseFullscreenEditor;
  }
}
